import {
    GetPatientDetailResponseIds,
    NullableOfTransmissionCanceledByIds,
    TransmissionStatusIds,
} from '@toktokdoctech/tokapi-client'
import dayjs from 'dayjs'
import { isInTimeRange } from '../../../utils/dates'

export const getPatientSexLabel = (sex?: string | null) => {
    if (sex?.toUpperCase() === 'M') return 'masculin'
    if (sex?.toUpperCase() === 'F') return 'féminin'
    return 'Non renseigné'
}

export const getPatientBirthLabel = (sex?: string | null) => {
    if (sex?.toUpperCase() === 'M') return 'Né'
    if (sex?.toUpperCase() === 'F') return 'Née'
    return 'Né(e)'
}

export const getPatientFormattedInfo = (patient: any | null) => {
  if (!patient)
    return ''
  const sex = patient?.patient?.sex ?? patient?.sex ?? null
  const birthdate = patient?.patient?.birthdate ?? patient?.birthdate ?? null
  const age = patient?.patient?.age ?? patient?.age

  if (!birthdate && birthdate !== 0)
    return ''
  const dateLabel = dayjs(birthdate).format('DD/MM/YYYY')
  const ageText = typeof age === 'number' && Number.isFinite(age) ? ` (${age} ans)` : ''

  return `${getPatientBirthLabel(sex)} le ${dateLabel}${ageText} - Sexe ${getPatientSexLabel(sex)}`
}

export const getCivilityLabel = (civility?: string | null) => {
    if (civility?.toUpperCase() === 'M') return 'M.'
    if (civility?.toUpperCase() === 'F') return 'Mᵐᵉ'
    return ''
}

export const isExpertUnavailableForToday = (periods?: { from: string; to: string }[]) => {
  if (!periods?.length)
    return false
  const now = dayjs()

  return periods.some((p) => {
    if (!p?.from || !p?.to)
      return false
    return isInTimeRange(now, p.from, p.to)
  })
}


export const normalizeMedicalTitleToSupR = (fullName?: string | null) => {
    if (!fullName) return ''
    const str = String(fullName)

    return str
        .replace(/^\s*Dr\s*\.?\s+/i, (m) => m.replace(/Dr\.?/i, 'Dʳ').replace(/\./g, ''))
        .replace(/^\s*Pr\s*\.?\s+/i, (m) => m.replace(/Pr\.?/i, 'Pʳ').replace(/\./g, ''))
}

export const normalizeRequesterLabel = (label?: string | null) => {
    const str = label ? String(label).trim() : ''

    if (!str) return ''
    if (/^(Dʳ|Pʳ)\b/.test(str)) return str
    return normalizeMedicalTitleToSupR(str)
}


export const getPatientBirthCity = (
    birthCity: GetPatientDetailResponseIds['birthCity'],
    countryBirth: GetPatientDetailResponseIds['countryBirth'],
) => `${birthCity?.label} (${birthCity?.code}), ${countryBirth?.name}`

export const getExpertiseText = (requiredExpert: any) => {
    if (!requiredExpert) return ''
    const profession = requiredExpert.profession?.trim?.() ?? requiredExpert.profession ?? ''
    const speciality = requiredExpert.speciality?.trim?.() ?? requiredExpert.speciality ?? ''
    if (profession && speciality) {
        return `${profession} - ${speciality}`
    }
    if (profession) {
        return profession
    }
    if (speciality) {
        return speciality
    }
    return ''
}

export const getTCHeaders = (isMinioEnabled: boolean) => {
    const result = [
        {
            label: 'Date',
        },
        {
            label: 'Téléconsultant',
        },
        {
            label: 'Spécialité',
        },
        {
            label: 'Documents patient joints',
        },
        {
            label: 'Facture',
        },
        {
            label: 'Ordonnance',
        },
        {
            label: 'Compte-rendu',
        },
    ]
    if (!isMinioEnabled) result.splice(3, 1)
    return result
}

export const getTleHeaders = (isMinioEnabled: boolean) => {
    const result = [
        {
            label: "Date d'envoi",
        },
        {
            label: "Date d'acquittement",
        },
        {
            label: 'Cible',
        },
        {
            label: 'Statut',
        },
        {
            label: 'Médecin requis',
        },
        {
            label: 'Spécialité',
        },
        {
            label: 'Documents patient joints',
        },
        {
            label: 'Ordonnance',
        },
        {
            label: 'Compte-rendu',
        },
    ]
    if (!isMinioEnabled) result.splice(6, 1)
    return result
}

export const getFormatedDateWithHours = (date: string) => dayjs(new Date(date)).format('DD/MM/YYYY - HH:mm')
export const getFormatedShortDate = (date: string) => dayjs(new Date(date)).format('DD_MM_YYYY')

export const renderTransmissionState = (
    status: TransmissionStatusIds,
    canceledBy: NullableOfTransmissionCanceledByIds,
) => {
    switch (status) {
        case TransmissionStatusIds.Draft:
            return 'Brouillon'
        case TransmissionStatusIds.Validating:
            return 'En attente de validation'
        case TransmissionStatusIds.Transmited:
            return 'Transmise'
        case TransmissionStatusIds.Canceled: {
            if (canceledBy === NullableOfTransmissionCanceledByIds.Requester) return 'Refusée par le requérant'
            if (canceledBy === NullableOfTransmissionCanceledByIds.RequiredExpert) return 'Refusée par le requis'
            if (canceledBy === NullableOfTransmissionCanceledByIds.Caregiver) return "Annulée par l'effecteur"
            return 'Refusée'
        }
        case TransmissionStatusIds.Finished:
            return 'Acquittée'
        default:
            return ''
    }
}
